import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  skipWhile,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { StateService } from 'src/app/auth/state.service';
import { ApiOrganization } from 'src/app/models_new/classes/api-models/ApiOrganization';
import { pagesPATH } from 'src/app/models_new/config/pages';
import { IHasuraUser } from 'src/app/models_new/types/hasura-user';
import { OrganizationLogoService } from 'src/app/services/organization-logo.service';
import { OrganizationType } from '../../../../models_new/enums/organization-type';
import { DataRequestState } from 'src/app/data-request/model';
import { InfoApiService } from 'src/app/services/api/info-api.service';
import { IButtonContent } from 'src/app/models_new/types/info-card-config';
import { toRequestState } from 'src/app/data-request/operators';
import { getElementByNameTag } from 'src/app/utils/contentful-utils';
import { settings } from 'src/app/models_new/config/application-settings';
import {
  LocalStorageService,
  StorageMethod,
} from 'src/app/services/local-storage.service';
import { LocalStorageKey } from 'src/app/models_new/enums/local-storage-keys';
import { UnitSystemType } from 'src/app/utils/unit-utils';
import { OrganizationApiService } from 'src/app/services/api/organization-api.service';
import { RXJSUtils } from 'src/app/utils/rxjs-utils';
import { ObjectUtils } from 'src/app/utils/object';

enum RoutesId {
  INVITE_PAGE = 'org_invite_page',
  CREATE_SALES_ORG = 'create_sales_org',
  EDIT_SALES_ORG = 'edit_sales_org',
}

@Component({
  selector: 'app-state-chips-component',
  templateUrl: './state-chips.component.html',
  styleUrls: ['./state-chips.component.scss'],
})
export class StateChipsComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  pagesPATH = pagesPATH;
  logoutPath = window.location.origin;
  routeId = RoutesId;
  eulaUrl = settings.eulaUrl;

  @Input() user: IHasuraUser;
  /**
   * @todo: Move the redundant customer_organizations$ call from ToolbarComponent
   * to be hosted here instead, as has no use in the parent component anymore.
   * @deprecated This input is deprecated and will be removed.
   */
  @Input() customer_organizations: ApiOrganization[];
  /**
   * @todo: Move the redundant sales_organizations$ call from ToolbarComponent
   * to be hosted here instead, as has no use in the parent component anymore.
   * @deprecated This input is deprecated and will be removed.
   */
  @Input() sales_organizations: ApiOrganization[];
  /**
   * @todo: Move the redundant sales_org$ call from ToolbarComponent
   * to be hosted here instead, as has no use in the parent component anymore.
   * @deprecated This input is deprecated and will be removed.
   */
  @Input() sales_org?: ApiOrganization;
  /**
   * @todo: Move the redundant customer_org$ call from ToolbarComponent
   * to be hosted here instead, as has no use in the parent component anymore.
   * @deprecated This input is deprecated and will be removed.
   */
  @Input() customer_org?: ApiOrganization;

  ready$: Observable<boolean> = of(false);

  // Menu-toggle states:
  userMenuClosed = true;
  salesOrgMenuClosed = true;
  customerOrgMenuClosed = true;

  // Menu item context:
  inviteCount$: Observable<number>;
  contentfulUserMenuButtons$: Observable<DataRequestState<IButtonContent[]>>;
  preferredUnitSystem: UnitSystemType = 'metric';

  // Organization-selector contexts:
  activeOrganization$: BehaviorSubject<ApiOrganization> = new BehaviorSubject(
    null
  );
  allUsersOrganizations: ApiOrganization[];
  unrelatedCustomerOrgs: ApiOrganization[];
  organizations: ApiOrganization[];
  filteredOrgs: ApiOrganization[];
  filteredSalesOrgs: ApiOrganization[];
  filteredUnrelatedOrgs: ApiOrganization[];

  constructor(
    private router: Router,
    public auth: AuthService,
    private infoApi: InfoApiService,
    private stateService: StateService,
    private localStorageService: LocalStorageService,
    private organizationApi: OrganizationApiService,
    public organizationLogoService: OrganizationLogoService
  ) {
    this.ready$ = this.stateService.isUserLogged$.pipe(
      skipWhile(
        (isUserLogged) => !isUserLogged && window.location.href.includes('o=1')
      ),
      switchMap((_) => this.stateService.sales_organization$),
      switchMap((salesOrg: ApiOrganization) => {
        this.sales_org = salesOrg;
        return combineLatest([
          this.stateService.organizations$,
          salesOrg
            ? this.organizationApi
                .getRelatedCustomerOrganizationsById(salesOrg?.id)
                .pipe(
                  takeUntil(this.destroy$),
                  map((orgs) =>
                    orgs.map(
                      (customer_org) => new ApiOrganization(customer_org)
                    )
                  )
                )
            : of(null),
          this.stateService
            .getCustomerOrSalesOrganization()
            .pipe(RXJSUtils.filterUndefinedAndNull()),
        ]);
      }),
      switchMap(([organizations, child_organizations, activeOrg]) => {
        if (organizations && activeOrg) {
          /** Gets the current active and unrealted organization & backs them up: */
          this.activeOrganization$.next(activeOrg);
          this.allUsersOrganizations = ObjectUtils.cloneObject(organizations);
          this.unrelatedCustomerOrgs = ObjectUtils.cloneObject(
            this.getUnrelatedCustomerOrganizations()
          );
          if (
            !this.sales_org &&
            activeOrg.type === OrganizationType.CUSTOMER_ORGANIZATION
          ) {
            /** Customer-organization-only user: */
            this.organizations = organizations;
          } else {
            /** Sales-organization user */
            this.organizations = child_organizations;
            this.filteredOrgs = ObjectUtils.cloneObject(this.organizations);
            this.filteredSalesOrgs = ObjectUtils.cloneObject(
              organizations.filter(
                (o) => o.type === OrganizationType.SALES_ORGANIZATION
              )
            );
            this.filteredUnrelatedOrgs = ObjectUtils.cloneObject(
              this.unrelatedCustomerOrgs
            );
          }
        }
        return of(true);
      })
    );
  }

  ngOnInit(): void {
    this.inviteCount$ = this.stateService.organizationInvites$.pipe(
      map((invites) => invites.length)
    );

    this.contentfulUserMenuButtons$ = this.infoApi
      .getButtonContentByTag('userMenuButton')
      .pipe(take(1), toRequestState());

    this.preferredUnitSystem = this.localStorageService.getData(
      LocalStorageKey.PREFERRED_UNIT_SYSTEM
    );
  }

  public getElementByNameTag(
    buttonElements: IButtonContent[],
    nameTag: string
  ): IButtonContent {
    return getElementByNameTag(buttonElements, nameTag);
  }

  public selectOrganization(org: ApiOrganization): void {
    if (org.type === OrganizationType.CUSTOMER_ORGANIZATION) {
      // If an unrelated customer organization is selected, unset the current sales organization:
      if (this.unrelatedCustomerOrgs.find((o) => o.id === org.id)) {
        this.stateService.setSalesOrganization(null);
      }
      this.stateService.setCustomerOrganization(org);
    } else {
      this.stateService.setSalesOrganization(org);
    }
    this.activeOrganization$.next(org);
  }

  public doAction(actionId: string, value: string): void {
    if (actionId === 'setUnitSystem') {
      this.localStorageService.setData(
        LocalStorageKey.PREFERRED_UNIT_SYSTEM,
        value,
        StorageMethod.LOCAL
      );
      window.location.reload();
    } else if (actionId === 'navigateTo') {
      const isExternal =
        value?.includes('http://') || value?.includes('https://');
      if (isExternal) {
        window.open(value, '_blank');
      } else {
        switch (value) {
          case RoutesId.INVITE_PAGE:
            this.router.navigate([pagesPATH.ORG_INVITE]);
            break;
          case RoutesId.CREATE_SALES_ORG:
            this.router.navigate([pagesPATH.USER, pagesPATH.NEW_ORGANIZATION]);
            break;
          case RoutesId.EDIT_SALES_ORG:
            this.router.navigate([
              pagesPATH.CUSTOMERS,
              pagesPATH.VIEW_ORG,
              this.sales_org.id,
            ]);
            break;
          default:
            console.warn(`Unknown navigation option for ID: ${value}`);
        }
      }
    }
  }

  /**
   * @desc Filters organizations by name, given a search string and updates the filteredOrgs
   * and filteredUnrelatedOrgs arrays accordingly.
   * @param {Event} event: The input event that triggered the filter.
   */
  public applyTextFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    if (filterValue) {
      this.filteredOrgs = [];
      this.filteredOrgs = this.organizations.filter((o) =>
        o.name.toLowerCase().includes(filterValue.toLowerCase())
      );
      this.filteredSalesOrgs = [];
      this.filteredSalesOrgs = this.allUsersOrganizations.filter((o) =>
        o.name.toLowerCase().includes(filterValue.toLowerCase())
      );
      this.filteredUnrelatedOrgs = [];
      this.filteredUnrelatedOrgs = this.unrelatedCustomerOrgs.filter((o) =>
        o.name.toLowerCase().includes(filterValue.toLowerCase())
      );
    } else {
      this.filteredOrgs = ObjectUtils.cloneObject(this.organizations);
      this.filteredSalesOrgs = ObjectUtils.cloneObject(
        this.allUsersOrganizations.filter(
          (o) => o.type === OrganizationType.SALES_ORGANIZATION
        )
      );
      this.filteredUnrelatedOrgs = ObjectUtils.cloneObject(
        this.unrelatedCustomerOrgs
      );
    }
  }

  /**
   * @function getUnrelatedCustomerOrganizations
   * @desc Gets unrelated (to salesOrgs) customerOrgs which the user have access to. Example scenarions;
   * - A SalesOrg/Backoffice user gets permission into another SalesOrg's customer-organization.
   * - A user gets permission into a customer with no sales organization associated.
   * If this is not handled, many organizations will lay unreachable.
   * @returns {ApiOrganizations[]} - A collection of unrelated customer organizations, if any.
   */
  private getUnrelatedCustomerOrganizations(): ApiOrganization[] {
    return this.allUsersOrganizations.filter((org) => {
      return org.type == OrganizationType.CUSTOMER_ORGANIZATION &&
        (!org['organization_relations'].length ||
          org['organization_relations'].filter(
            (relation) =>
              this.allUsersOrganizations.find(
                (orgI) => orgI.id === relation.organization_a_id
              ) == undefined
          ).length)
        ? org
        : null;
    });
  }
}
